import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { ActionCard, DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, Section, useAuth } from "@ozpanel/ui";

// The exposure scanner (internal/modules/exposure, 2026-10-21): what the public web can fetch from a
// hosted site that it should not — a .env, a dump, source control, a key in a served file. Its two
// routes had no console: GET /exposure/findings (the operator sees every account's) and POST
// /exposure/scan, which runs the scan now on THIS node and WAITS for it (minutes). The scan is
// read-only — it fetches and reads, and changes nothing it finds — so the action is low danger and the
// page says what to do about a finding instead (remove the file, or rotate the key).

/** exposure.go findingRow — Finding's tags plus the row's own, exactly. */
interface FindingRow {
  id: number;
  tenant_id: number;
  site: string;
  kind: string;
  target: string;
  severity: string;
  detail: string;
  first_seen: string;
  last_seen: string;
  resolved_at?: string;
}
interface Findings {
  findings: FindingRow[] | null;
  open: Record<string, number> | null;
  note?: string;
  last_scan?: string;
  last_scan_note?: string;
}

const sevTone = (s: string) => (s === "urgent" ? "bad" : s === "warn" ? "warn" : "neutral");

export function ExposureOps() {
  const { client } = useAuth();
  const [all, setAll] = React.useState(false);
  const q = useQuery({
    queryKey: ["exposure-findings", all],
    queryFn: () => client.apiFetch<Findings>(`/exposure/findings${all ? "?all=1" : ""}`),
    refetchInterval: 120000,
  });
  const d = q.data;
  const rows = d?.findings ?? [];
  const open = d?.open ?? {};

  const columns: DataListColumn<FindingRow>[] = [
    {
      key: "severity",
      label: "Severity",
      value: (f) => f.severity,
      render: (f) => <StatusPill tone={f.resolved_at ? "ok" : sevTone(f.severity)}>{f.resolved_at ? "resolved" : f.severity}</StatusPill>,
    },
    { key: "site", label: "Site", value: (f) => f.site, render: (f) => <span className="font-mono text-[12px]">{f.site}</span> },
    { key: "kind", label: "Kind", value: (f) => f.kind },
    {
      key: "target",
      label: "What",
      value: (f) => f.target,
      render: (f) => (
        <span className="text-[12px]">
          <span className="block font-mono break-all">{f.target}</span>
          <span className="block text-[11.5px] text-muted">{f.detail}</span>
        </span>
      ),
    },
    { key: "tenant_id", label: "Account", value: (f) => f.tenant_id },
    {
      key: "last_seen",
      label: "Last seen",
      value: (f) => f.last_seen,
      render: (f) => <span className="text-[12px]">{new Date(f.last_seen).toLocaleString()}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <Section
        title="Exposure findings"
        actions={
          <span className="flex items-center gap-2">
            {(["urgent", "warn", "info"] as const).map((s) =>
              open[s] ? (
                <StatusPill key={s} tone={sevTone(s)}>
                  {open[s]} {s}
                </StatusPill>
              ) : null,
            )}
            <Button size="sm" variant="ghost" onClick={() => setAll((v) => !v)}>
              {all ? "Open only" : "Include resolved"}
            </Button>
          </span>
        }
        bodyClassName="p-0"
      >
        <div className="p-4">
          <p className="mb-3 text-[12px] leading-relaxed text-muted">
            {d?.last_scan ? <>Last scan finished {new Date(d.last_scan).toLocaleString()}. </> : <>No scan has finished yet. </>}
            {d?.last_scan_note && <span className="font-mono text-[11.5px]">{d.last_scan_note} </span>}
            {d?.note}
          </p>
          <DataList
            items={rows}
            columns={columns}
            getRowKey={(f) => String(f.id)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the exposure findings"
            emptyMessage={all ? "The scanner has never found anything." : "Nothing open: no site serves anything it should not."}
            emptyHint={d?.last_scan ? undefined : "Findings appear after the first scan finishes."}
          />
        </div>
      </Section>

      <Section title="Scan">
        <ActionCard
          title="Scan every site now"
          description="Runs the exposure scan on this node over every enabled site: fetches the paths the internet tries and reads each site's folder. Read-only — it changes nothing it finds. It waits for the scan to finish, which takes minutes on a node with many sites; the findings above refresh when it does."
          actionLabel="Scan now"
          run={() => client.apiFetch<{ node: string; summary: string }>("/exposure/scan", { method: "POST" })}
          invalidates={[["exposure-findings"]]}
          renderResult={(x) => {
            const r = x as { node?: string; summary?: string };
            return (
              <div className="text-[12px]">
                {r.node && <span className="text-muted">{r.node}: </span>}
                {r.summary}
              </div>
            );
          }}
        />
      </Section>
    </div>
  );
}
